import React, { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useLanguage } from '../contexts/LanguageContext';

const VerticalFader = ({ label, value, onChange, min = 0, max = 100, step = 1 }) => {
  const { t } = useLanguage();
  const [isDragging, setIsDragging] = useState(false);
  const faderRef = useRef(null);
  const knobRef = useRef(null);
  const containerHeight = 160; // Fixed height for better precision
  
  // Ensure the value is properly capped between min and max
  const normalizedValue = Math.max(min, Math.min(max, value || 0));
  const displayValue = Math.round(normalizedValue);
  
  // Calculate the position of the fader knob
  const valueRange = max - min;
  const knobPosition = containerHeight - (((normalizedValue - min) / valueRange) * containerHeight);
  
  // Handle mouse/touch down on the fader knob
  const handleKnobDown = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
    
    // Store initial positions to calculate relative movement
    const clientY = e.type.includes('touch') ? e.touches[0].clientY : e.clientY;
    knobRef.current = { 
      startY: clientY,
      startValue: normalizedValue
    };
  };

  // Handle mouse/touch move while dragging
  const handleMouseMove = (e) => {
    if (!isDragging || !knobRef.current || !faderRef.current) return;

    const clientY = e.type.includes('touch') ? e.touches[0].clientY : e.clientY;
    const deltaY = knobRef.current.startY - clientY;
    const rect = faderRef.current.getBoundingClientRect();
    
    // Calculate movement as a percentage of the container height
    const movementPercent = (deltaY / rect.height) * valueRange;
    
    // Calculate new value based on the initial value and the movement
    const newValue = knobRef.current.startValue + movementPercent;
    const clampedValue = Math.max(min, Math.min(max, newValue));
    
    onChange(clampedValue);
  };

  // Handle mouse/touch up to end dragging
  const handleMouseUp = () => {
    setIsDragging(false);
    knobRef.current = null;
  };

  // Handle direct click on the fader track
  const handleTrackClick = (e) => {
    if (!faderRef.current) return;
    
    // Prevent handling if we're clicking on the knob (should use drag instead)
    if (e.target.closest('.fader-knob')) return;
    
    const rect = faderRef.current.getBoundingClientRect();
    const clickY = e.clientY - rect.top;
    const percentage = 1 - (clickY / rect.height);
    const newValue = min + (percentage * valueRange);
    
    onChange(Math.max(min, Math.min(max, newValue)));
  };

  // Handle touch events for the track
  const handleTrackTouch = (e) => {
    if (!faderRef.current) return;
    
    // Prevent handling if we're touching the knob (should use drag instead)
    if (e.target.closest('.fader-knob')) return;
    
    const rect = faderRef.current.getBoundingClientRect();
    const touchY = e.touches[0].clientY - rect.top;
    const percentage = 1 - (touchY / rect.height);
    const newValue = min + (percentage * valueRange);
    
    onChange(Math.max(min, Math.min(max, newValue)));
  };

  // Add and remove event listeners
  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.addEventListener('touchmove', handleMouseMove, { passive: false });
      document.addEventListener('touchend', handleMouseUp);
      
      // Prevent default behavior to avoid page scrolling while dragging
      const preventDefault = (e) => {
        if (isDragging) e.preventDefault();
      };
      document.addEventListener('touchmove', preventDefault, { passive: false });
      
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        document.removeEventListener('touchmove', handleMouseMove);
        document.removeEventListener('touchend', handleMouseUp);
        document.removeEventListener('touchmove', preventDefault);
      };
    }
  }, [isDragging, valueRange]);

  // Calculate the percentage for the colored track
  const trackFillPercentage = ((normalizedValue - min) / valueRange) * 100;
  
  // Define db values for scale (typical mixer scale)
  const dbMarks = [0, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100];
  
  return (
    <div className="flex flex-col items-center">
      {/* Value Display */}
      <div className="text-sm font-semibold text-primary-600 dark:text-primary-400 bg-primary-50 dark:bg-primary-900/20 px-3 py-1 rounded-full mb-2 min-w-[3rem] text-center">
        {displayValue}
      </div>
      
      {/* Fader Container */}
      <div className="flex items-center justify-center">
        {/* Left scale numbers */}
        <div className="flex flex-col justify-between h-[160px] mr-1">
          {[100, 0].map(val => (
            <div 
              key={`scale-${val}`}
              className="text-xs text-gray-500 dark:text-gray-400 font-medium"
            >
              {val}
            </div>
          ))}
        </div>
        
        {/* Fader track and knob */}
        <div 
          ref={faderRef}
          className="relative h-[160px] w-14 cursor-pointer"
          onClick={handleTrackClick}
          onTouchStart={handleTrackTouch}
        >
          {/* Main fader track - outer shell */}
          <div className="absolute inset-x-5 inset-y-0 rounded-lg bg-gradient-to-r from-gray-300 via-gray-200 to-gray-300 dark:from-gray-700 dark:via-gray-600 dark:to-gray-700 shadow-inner">
            {/* Inner track channel - the slot where the fader moves */}
            <div className="absolute inset-x-[3px] inset-y-0 bg-gradient-to-b from-gray-700 via-gray-800 to-gray-700 dark:from-gray-900 dark:via-black dark:to-gray-900 rounded">
              {/* Scale markers */}
              {dbMarks.map(mark => (
                <div 
                  key={`db-${mark}`}
                  className={`absolute left-0 right-0 h-[2px] ${
                    mark % 20 === 0 
                      ? 'bg-gray-400 dark:bg-gray-500' 
                      : mark % 10 === 0 
                        ? 'bg-gray-300 dark:bg-gray-600' 
                        : 'bg-gray-200 dark:bg-gray-700'
                  }`}
                  style={{ bottom: `${mark}%` }}
                />
              ))}
              
              {/* Colored fill to show volume level */}
              <div 
                className="absolute left-0 right-0 bottom-0 bg-gradient-to-t from-blue-500/90 to-indigo-500/90 rounded-b"
                style={{ height: `${trackFillPercentage}%` }}
              />
              
              {/* Complete black background to ensure consistent appearance */}
              <div className="absolute inset-0 -z-10 bg-black rounded" />
              
              {/* Light reflection effect */}
              <div className="absolute inset-x-[30%] top-0 bottom-0 bg-gradient-to-r from-transparent via-white/5 to-transparent" />
            </div>
          </div>
          
          {/* Fader knob */}
          <motion.div
            className="fader-knob absolute left-0 right-0 h-7 z-10 cursor-grab active:cursor-grabbing"
            style={{ top: `${Math.max(0, Math.min(containerHeight - 28, knobPosition - 14))}px` }} /* Center the knob on the position with boundaries */
            onMouseDown={handleKnobDown}
            onTouchStart={handleKnobDown}
            whileTap={{ scale: 0.98 }}
          >
            {/* Knob base */}
            <div className="absolute inset-x-0 h-full bg-gradient-to-b from-gray-100 to-gray-300 dark:from-gray-600 dark:to-gray-800 rounded-md shadow-lg border border-gray-400 dark:border-gray-500 overflow-hidden">
              {/* Grip lines */}
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="flex space-x-1.5">
                  {[...Array(5)].map((_, i) => (
                    <div 
                      key={i} 
                      className="w-0.5 h-3 bg-gray-400 dark:bg-gray-300 rounded-full"
                    />
                  ))}
                </div>
              </div>
              
              {/* Light reflection */}
              <div className="absolute inset-x-0 top-0 h-[40%] bg-gradient-to-b from-white/30 to-transparent dark:from-white/10" />
            </div>
            
            {/* Knob indicator line */}
            <div className="absolute top-1/2 left-1/2 w-1 h-full bg-primary-500 dark:bg-primary-400 -translate-x-1/2 -translate-y-1/2" />
          </motion.div>
        </div>
      </div>
      
      {/* Label */}
      {label && (
        <div className="text-xs text-gray-600 dark:text-gray-400 mt-2 text-center font-medium">
          {label}
        </div>
      )}
    </div>
  );
};

export default VerticalFader;